import { useState } from "react";
import CandleChart from "@/components/CandleChart";
import { EventLog, Side, TradesTable } from "@/components/Bits";
import { inr, px, sign } from "@/lib/api";

export function stockChartProps(stock, events, trades, candles, tgtLabel = "2R") {
  const ev = events.filter((e) => e.sym === stock.sym);
  const tr = trades.filter((t) => t.sym === stock.sym).pop();
  const oi = ev.map((e) => !!e.order).lastIndexOf(true);
  const lastOrder = oi >= 0 ? ev[oi].order : null;
  const endEv = oi >= 0 ? ev.slice(oi + 1).find((e) => ["cancel", "expire", "fill", "triggered"].includes(e.kind)) : null;
  const dead = endEv && (endEv.kind === "cancel" || endEv.kind === "expire");
  const lastT = candles[candles.length - 1]?.t;
  const fillT = tr?.t_in, exitT = tr?.legs?.[tr.legs.length - 1]?.t;
  const levels = stock.or_high == null ? [] : [
    { y: stock.or_high, color: "#94A3B8", label: "ORH", dash: true },
    { y: stock.or_low, color: "#94A3B8", label: "ORL", dash: true },
  ];
  if (lastOrder) {
    const from = lastOrder.sig.t, to = endEv?.t || lastT;
    levels.push({ y: lastOrder.trigger, color: "#06B6D4", label: dead ? "TRG ✕" : "TRG", from, to });
    if (!tr) levels.push({ y: lastOrder.stop, color: "#EF4444", label: dead ? "SL ✕" : "SL", from, to });
  }
  if (tr) {
    levels.push({ y: tr.init_stop, color: "#EF4444", label: "SL", from: fillT, to: exitT || lastT });
    levels.push({ y: tr.target, color: "#10B981", label: tgtLabel, from: fillT, to: exitT || lastT });
  }
  const marks = [];
  ev.forEach((e) => {
    if (e.kind === "break") marks.push({ t: e.t, color: "#8B5CF6", label: "BRK" });
    if (e.kind === "signal" || e.kind === "shift") marks.push({ t: e.order.sig.t, color: "#06B6D4", label: "SIG" });
    if (e.kind === "cancel" || e.kind === "expire") marks.push({ t: e.t, color: "#F87171", label: "CXL" });
    if (e.kind === "fill") marks.push({ t: e.t, color: "#F8FAFC", label: "IN" });
    if (e.kind === "partial" || e.kind === "exit") marks.push({ t: e.t, color: "#F59E0B", label: "OUT" });
  });
  return { candles, levels, marks };
}

export default function DayReplay({ day }) {
  const [sel, setSel] = useState(day.stocks?.[0]?.sym);
  const stock = day.stocks?.find((s) => s.sym === sel);
  return (
    <div className="space-y-3" data-testid={`day-replay-${day.date}`}>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
        <div className="panel px-3 py-2"><div className="label-xs">A/D</div><div className="num text-slate-100">{day.ad ? `${day.ad.advances}/${day.ad.declines} = ${day.ad.ratio}` : "—"}</div></div>
        <div className="panel px-3 py-2"><div className="label-xs">Bias</div><Side side={day.bias} /></div>
        <div className="panel px-3 py-2"><div className="label-xs">Sector</div><div className="text-slate-100">{day.sector || "—"}</div></div>
        <div className="panel px-3 py-2"><div className="label-xs">Trades</div><div className="num text-slate-100">{day.trades.length}</div></div>
        <div className="panel px-3 py-2"><div className="label-xs">Net</div><div className={`num ${sign(day.net)}`}>{inr(day.net)}</div></div>
      </div>
      {day.reason && !day.trades.length && <div className="text-xs text-amber-300 border border-amber-500/30 bg-amber-500/5 rounded-md px-3 py-2" data-testid="day-reason">No trade: {day.reason}</div>}
      {!!day.stocks?.length && (
        <div className="flex flex-wrap gap-1.5">
          {day.stocks.map((s) => (
            <button key={s.sym} onClick={() => setSel(s.sym)} data-testid={`replay-stock-${s.sym}`}
              className={`px-2.5 py-1 rounded-md text-xs num border transition-colors ${sel === s.sym ? "border-cyan-500/60 text-cyan-300 bg-cyan-500/5" : "border-[#1E293B] text-slate-400 hover:text-slate-100"}`}>
              {s.sym} <span className={sign(s.pct)}>{s.pct > 0 ? "+" : ""}{s.pct}%</span>
            </button>
          ))}
        </div>
      )}
      {stock && (
        <div className="panel p-2">
          <div className="flex flex-wrap gap-4 px-2 pb-1 text-[11px] num text-slate-400">
            <span>OR {px(stock.or_low)}–{px(stock.or_high)}</span><span>VWAP@09:30 {px(stock.vwap)}</span><span>lot {stock.lot}</span>
          </div>
          <CandleChart {...stockChartProps(stock, day.events, day.trades, day.charts?.[stock.sym] || [], "2R")} testId="replay-chart" />
        </div>
      )}
      <TradesTable trades={day.trades} testId="day-trades" targetLabel="2R" />
      <div className="panel p-3"><div className="label-xs mb-2">Event log</div><EventLog events={day.events} testId="day-events" /></div>
    </div>
  );
}
